import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { fr as frLocale, enUS } from "date-fns/locale";
import { CalendarIcon, Check, Minus, Plus, Info } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../i18n/LanguageContext";
import { ACTIVITIES } from "../data/mock";

const BookingContext = createContext(null);
export const useBooking = () => {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used within BookingProvider");
  return ctx;
};

const emptyForm = {
  name: "",
  email: "",
  phone: "",
  activityId: "",
  optionIdx: 0,
  date: undefined,
  people: 2,
  children: 0,
  addons: [],
  message: "",
  accept: false,
};

export const BookingProvider = ({ children }) => {
  const { t, lang } = useLang();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deposits, setDeposits] = useState({});
  const [showCal, setShowCal] = useState(false);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    axios
      .get(`${API}/activities-deposits`)
      .then((res) => {
        const map = {};
        res.data.forEach((d) => (map[d.id] = d));
        setDeposits(map);
      })
      .catch(() => {});
  }, [API]);

  const openBooking = useCallback((activityId = "") => {
    setForm({ ...emptyForm, activityId: activityId || "" });
    setSuccess(false);
    setShowCal(false);
    setOpen(true);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const activity = ACTIVITIES.find((a) => a.id === form.activityId);
  const options = activity?.options || [];
  const selectedOption = options[form.optionIdx] || options[0];
  const isGroup = !!activity?.groupPricing;
  const childDiscount = activity?.childDiscount || 0;
  const hasChildren = childDiscount > 0;
  const addonDefs = activity?.addons || [];
  const maxPeople = activity?.maxPeople || 50;

  const adults = form.people;
  const childCount = hasChildren ? form.children : 0;
  const optEur = selectedOption?.eur || 0;
  const optDhs = selectedOption?.dhs || 0;

  const selectedAddons = addonDefs.filter((a) => form.addons.includes(a.id));
  const addonsEur = selectedAddons.reduce((s, a) => s + a.eur, 0);
  const addonsDhs = selectedAddons.reduce((s, a) => s + a.dhs, 0);

  let baseEur, baseDhs;
  if (isGroup) {
    baseEur = optEur;
    baseDhs = optDhs;
  } else if (hasChildren) {
    baseEur = adults * optEur + childCount * optEur * (1 - childDiscount);
    baseDhs = adults * optDhs + childCount * optDhs * (1 - childDiscount);
  } else {
    baseEur = optEur * adults;
    baseDhs = optDhs * adults;
  }
  const totalEur = Math.round((baseEur + addonsEur) * 100) / 100;
  const totalDhs = Math.round(baseDhs + addonsDhs);

  const depositCfg = deposits[form.activityId];
  const optOverride = depositCfg && depositCfg.options ? depositCfg.options[String(form.optionIdx)] : null;
  const effType = optOverride?.type || depositCfg?.type;
  const effValue = optOverride && optOverride.value != null ? optOverride.value : depositCfg?.value;
  const depositPerPerson = depositCfg
    ? effType === "percent"
      ? Math.round(optEur * effValue / 100)
      : effValue
    : null;
  let depositTotal = null;
  if (depositPerPerson != null) {
    if (isGroup) depositTotal = depositPerPerson;
    else if (hasChildren) depositTotal = depositPerPerson * adults + depositPerPerson * (1 - childDiscount) * childCount;
    else depositTotal = depositPerPerson * adults;
    depositTotal = Math.round(depositTotal * 100) / 100;
  }

  const toggleAddon = (id) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(id) ? f.addons.filter((x) => x !== id) : [...f.addons, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.activityId || !form.date) {
      toast({ title: t.booking.required, variant: "destructive" });
      return;
    }
    if (!form.accept) {
      toast({ title: t.booking.mustAccept, variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const pre = await axios.post(`${API}/prebookings`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        activity_id: form.activityId,
        activity_title: activity?.title[lang] || "",
        option_label: selectedOption?.label[lang] || "",
        option_index: form.optionIdx,
        option_dhs: selectedOption?.dhs || null,
        option_eur: selectedOption?.eur || null,
        date: form.date ? format(form.date, "yyyy-MM-dd") : null,
        people: adults + childCount,
        adults,
        children: childCount,
        child_discount: childDiscount,
        pricing_mode: isGroup ? "group" : "per_person",
        addons: selectedAddons.map((a) => ({ id: a.id, label: a.label[lang], eur: a.eur, dhs: a.dhs })),
        message: form.message || "",
      });
      const { id } = pre.data;
      const checkout = await axios.post(`${API}/payments/checkout`, {
        origin_url: window.location.origin,
        prebooking_id: id,
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      setSubmitting(false);
      toast({ title: t.booking.errorGeneric, variant: "destructive" });
    }
  };

  const locale = lang === "fr" ? frLocale : enUS;

  return (
    <BookingContext.Provider value={{ openBooking }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[540px] max-h-[92vh] overflow-y-auto bg-[#f7f5ef] border-[#e3e0d6] p-0 gap-0">
          {success ? (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#7c8f74] mx-auto flex items-center justify-center mb-6">
                <Check size={32} className="text-white" />
              </div>
              <h3 className="font-serif text-3xl text-[#20261f] mb-3">{t.booking.successTitle}</h3>
              <p className="text-[#6b7166] leading-relaxed mb-8">{t.booking.successText}</p>
              <Button
                onClick={() => setOpen(false)}
                className="bg-[#7c8f74] hover:bg-[#697c62] text-white rounded-full px-8"
              >
                OK
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader className="px-7 pt-7 pb-4 text-left">
                <DialogTitle className="font-serif text-3xl text-[#20261f] font-normal">
                  {t.booking.title}
                </DialogTitle>
                <DialogDescription className="text-[#6b7166]">
                  {t.booking.subtitle}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="px-7 pb-7 space-y-4">
                <div className="flex items-start gap-3 bg-[#eef0e9] border border-[#dde2d5] rounded-lg px-4 py-3">
                  <Info size={18} className="text-[#5c6b54] shrink-0 mt-0.5" />
                  <p className="text-[12px] leading-relaxed text-[#4a5045]">{t.booking.notice}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.name} *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder={t.booking.namePh}
                      className="bg-white border-[#dedbd0] focus-visible:ring-[#7c8f74]"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.phone} *</Label>
                    <Input
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder={t.booking.phonePh}
                      className="bg-white border-[#dedbd0] focus-visible:ring-[#7c8f74]"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.email} *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => set("email", e.target.value)}
                    placeholder={t.booking.emailPh}
                    className="bg-white border-[#dedbd0] focus-visible:ring-[#7c8f74]"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.activity} *</Label>
                  <Select
                    value={form.activityId}
                    onValueChange={(v) => { set("activityId", v); set("optionIdx", 0); set("children", 0); set("addons", []); }}
                  >
                    <SelectTrigger className="bg-white border-[#dedbd0] focus:ring-[#7c8f74]">
                      <SelectValue placeholder={t.booking.activity} />
                    </SelectTrigger>
                    <SelectContent className="bg-white max-h-64">
                      {ACTIVITIES.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.title[lang]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {options.length > 1 && (
                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.option}</Label>
                    <Select
                      value={String(form.optionIdx)}
                      onValueChange={(v) => set("optionIdx", Number(v))}
                    >
                      <SelectTrigger className="bg-white border-[#dedbd0] focus:ring-[#7c8f74]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        {options.map((o, i) => (
                          <SelectItem key={i} value={String(i)}>
                            {o.label[lang]} — {o.dhs} DHS ({o.eur}€)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.date} *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowCal((s) => !s)}
                      className="w-full justify-start text-left font-normal bg-white border-[#dedbd0] hover:bg-white"
                    >
                      <CalendarIcon size={16} className="mr-2 text-[#7c8f74]" />
                      {form.date ? format(form.date, "PPP", { locale }) : (
                        <span className="text-[#9aa08f]">{t.booking.pickDate}</span>
                      )}
                    </Button>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{hasChildren ? t.booking.adults : t.booking.people}</Label>
                    <div className="flex items-center bg-white border border-[#dedbd0] rounded-md h-10">
                      <button
                        type="button"
                        onClick={() => set("people", Math.max(1, form.people - 1))}
                        className="px-3 h-full text-[#5c6b54] hover:text-[#20261f]"
                      >
                        <Minus size={15} />
                      </button>
                      <span className="flex-1 text-center text-sm font-medium text-[#20261f]">{form.people}</span>
                      <button
                        type="button"
                        onClick={() => set("people", Math.min(maxPeople, form.people + 1))}
                        className="px-3 h-full text-[#5c6b54] hover:text-[#20261f]"
                      >
                        <Plus size={15} />
                      </button>
                    </div>
                  </div>
                </div>

                {showCal && (
                  <div className="rounded-xl border border-[#dedbd0] bg-white flex justify-center">
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(d) => {
                        set("date", d);
                        setShowCal(false);
                      }}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </div>
                )}

                {hasChildren && (
                  <div className="space-y-1.5">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.children}</Label>
                    <div className="flex items-center bg-white border border-[#dedbd0] rounded-md h-10 max-w-[180px]">
                      <button
                        type="button"
                        onClick={() => set("children", Math.max(0, form.children - 1))}
                        className="px-3 h-full text-[#5c6b54] hover:text-[#20261f]"
                        data-testid="booking-children-minus"
                      >
                        <Minus size={15} />
                      </button>
                      <span className="flex-1 text-center text-sm font-medium text-[#20261f]" data-testid="booking-children-count">{form.children}</span>
                      <button
                        type="button"
                        onClick={() => set("children", form.children + 1)}
                        className="px-3 h-full text-[#5c6b54] hover:text-[#20261f]"
                        data-testid="booking-children-plus"
                      >
                        <Plus size={15} />
                      </button>
                    </div>
                  </div>
                )}

                {addonDefs.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.addons}</Label>
                    <div className="space-y-2">
                      {addonDefs.map((a) => (
                        <label
                          key={a.id}
                          className="flex items-center justify-between gap-3 bg-white border border-[#dedbd0] rounded-lg px-4 py-2.5 cursor-pointer hover:border-[#7c8f74] transition"
                          data-testid={`booking-addon-${a.id}`}
                        >
                          <span className="flex items-center gap-3">
                            <Checkbox
                              checked={form.addons.includes(a.id)}
                              onCheckedChange={() => toggleAddon(a.id)}
                              className="border-[#b7c1ad] data-[state=checked]:bg-[#7c8f74] data-[state=checked]:border-[#7c8f74]"
                            />
                            <span className="text-sm text-[#3d443a]">{a.label[lang]}</span>
                          </span>
                          <span className="text-sm font-medium text-[#20261f] whitespace-nowrap">+{a.dhs} DHS · {a.eur}€</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-[#3d443a] text-xs uppercase tracking-wide">{t.booking.message}</Label>
                  <Textarea
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                    placeholder={t.booking.messagePh}
                    rows={2}
                    className="bg-white border-[#dedbd0] focus-visible:ring-[#7c8f74] resize-none"
                  />
                </div>

                {selectedOption && (
                  <div className="flex items-center justify-between bg-[#eef0e9] rounded-lg px-4 py-3">
                    <span className="text-xs uppercase tracking-wide text-[#5c6b54]">{t.booking.total}</span>
                    <span className="font-serif text-xl text-[#20261f]">
                      {totalDhs} DHS <span className="text-sm text-[#6b7166]">≈ {totalEur}€</span>
                    </span>
                  </div>
                )}

                {depositCfg && depositTotal != null && (
                  <div className="bg-[#20261f] rounded-lg px-4 py-3.5 text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-xs uppercase tracking-wide text-[#b6c3ab]">
                        {t.booking.depositLabel}
                      </span>
                      <span className="font-serif text-2xl">{depositTotal} €</span>
                    </div>
                    <p className="text-[11px] text-white/60 mt-1.5">{t.booking.balanceNote}</p>
                  </div>
                )}

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={form.accept}
                    onCheckedChange={(v) => set("accept", v === true)}
                    className="mt-0.5 border-[#b7c1ad] data-[state=checked]:bg-[#7c8f74] data-[state=checked]:border-[#7c8f74]"
                  />
                  <span className="text-[12px] leading-relaxed text-[#4a5045]">{t.booking.disclaimer}</span>
                </label>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#7c8f74] hover:bg-[#697c62] text-white rounded-full py-6 text-sm tracking-[0.1em] uppercase"
                >
                  {submitting ? t.booking.redirecting : t.booking.submit}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </BookingContext.Provider>
  );
};
